/**
 * Stable unique identifiers.
 *
 * Replaces two collision-prone patterns from the prototype:
 *
 *  1. `Date.now()` as a primary key for songs, duplicates, and activity
 *     entries. Two actions inside the same millisecond produced the same
 *     key, so React reconciled the wrong rows and lookups by id resolved
 *     to whichever record happened to come first.
 *
 *  2. `` `v${versions.length + 1}` `` for version ids. With v1, v2, v3,
 *     deleting v2 leaves length 2, so the next version was *also* v3 —
 *     duplicate React keys, and restore/delete/compare all targeting the
 *     wrong record.
 */

/** Monotonic fallback counter, used only when crypto.randomUUID is absent. */
let fallbackCounter = 0;

/**
 * Returns a unique id string.
 *
 * Uses crypto.randomUUID when available (all current browsers, secure
 * contexts only). Falls back to a counter + timestamp + random suffix so
 * ids stay unique on http:// origins and in older runtimes.
 *
 */
export function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  fallbackCounter += 1;
  const rand = Math.random().toString(36).slice(2, 10);
  return `id-${Date.now().toString(36)}-${fallbackCounter}-${rand}`;
}

/**
 * Returns a unique id with a readable prefix, e.g. `song-3f2a…`.
 * The prefix is cosmetic — useful when inspecting state in devtools.
 *
 */
export function prefixedId(prefix: string): string {
  return `${prefix}-${newId()}`;
}
